import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../utils/api';
import { CheckCircle, Loader2, Smartphone, ShieldAlert } from 'lucide-react';

const DEVICE_TOKEN_KEY = 'qr_device_token';

export default function QRApprove() {
  const [params] = useSearchParams();
  const sessionToken = params.get('session');

  const [state, setState] = useState('loading'); // loading | password | success | error
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!sessionToken) {
      setState('error');
      setErrorMsg('Invalid link — no session token found.');
      return;
    }
    attemptAutoApprove();
  }, []);

  const attemptAutoApprove = async () => {
    const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY);
    if (!deviceToken) {
      // No registered device — need password
      setState('password');
      return;
    }
    try {
      const res = await api.post('/auth/qr-approve', {
        session_token: sessionToken,
        device_token: deviceToken,
      });
      if (res.data.authenticated) {
        setState('success');
      } else if (res.data.needs_password) {
        setState('password');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (detail.includes('expired')) {
        setState('error');
        setErrorMsg('QR session expired. Go back to your desktop and refresh the login page.');
      } else {
        // Device token might be revoked — fall back to password
        setState('password');
      }
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Enter email and password'); return; }
    setLoading(true);
    try {
      const ua = navigator.userAgent;
      let label = 'Phone';
      if (/iPhone/.test(ua)) label = 'iPhone';
      else if (/Android/.test(ua)) label = 'Android';
      else if (/iPad/.test(ua)) label = 'iPad';
      const now = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      label += ` - added ${now}`;

      const res = await api.post('/auth/qr-approve', {
        session_token: sessionToken,
        email,
        password,
        device_label: label,
      });
      if (res.data.authenticated) {
        // Store the new device token for future scans
        if (res.data.device_token) {
          localStorage.setItem(DEVICE_TOKEN_KEY, res.data.device_token);
        }
        setState('success');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6" style={{ background: 'var(--bg-void)' }}>
        <img src="/workforce-logo.png" alt="WorkForce" style={{ height: '56px' }} />
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--amber)' }} />
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Approving desktop login...</p>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6" style={{ background: 'var(--bg-void)' }}>
        <CheckCircle className="h-16 w-16" style={{ color: 'var(--green)' }} />
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>Desktop Signed In</h1>
        <p className="text-sm text-center" style={{ color: 'var(--text-secondary)' }}>
          Your desktop is now logged in. You can close this page.
        </p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6" style={{ background: 'var(--bg-void)' }}>
        <ShieldAlert className="h-12 w-12" style={{ color: 'var(--red)' }} />
        <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>Failed</h1>
        <p className="text-sm text-center" style={{ color: 'var(--text-muted)' }}>{errorMsg}</p>
      </div>
    );
  }

  // state === 'password' — new phone, needs to register
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg-void)' }}>
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <Smartphone className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--amber)' }} />
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>Confirm Desktop Login</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Sign in to approve the desktop session and register this phone for quick login next time.
          </p>
        </div>
        <form onSubmit={handlePasswordSubmit} className="space-y-4 rounded-xl p-6" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fd)' }}>Email</label>
            <Input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com" className="h-11"
              style={{ background: 'var(--bg-void)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="qr-approve-email" autoFocus
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fd)' }}>Password</label>
            <Input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password" className="h-11"
              style={{ background: 'var(--bg-void)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="qr-approve-password"
            />
          </div>
          <Button
            type="submit" disabled={loading}
            className="w-full h-12 font-bold uppercase tracking-wider"
            style={{ background: 'var(--amber)', color: 'var(--bg-void)', fontFamily: 'var(--fd)' }}
            data-testid="qr-approve-submit"
          >
            {loading ? 'Approving...' : 'Approve & Register Phone'}
          </Button>
        </form>
      </div>
    </div>
  );
}
